import type { PacingTargetsConfig } from "@expert/app-config";
import type { CallCategory } from "@expert/shared-types";

interface PacingInput {
    pacingConfig: PacingTargetsConfig | undefined;
    gaiaCallCategory: CallCategory | undefined;
    salesEligible: boolean;
    eligibleProductCount: number;
    hasPrimaryProduct: boolean;
    actualSeconds: number | undefined;
}

export function buildPacingFields({
    pacingConfig,
    gaiaCallCategory,
    salesEligible,
    eligibleProductCount,
    hasPrimaryProduct,
    actualSeconds,
}: PacingInput): Record<string, unknown> {
    if (!pacingConfig) return {};

    const categoryTarget = gaiaCallCategory ? pacingConfig.callTypeTargets[gaiaCallCategory] : undefined;

    // When pacing is disabled, emit baseline timing fields for pilot vs non-pilot comparison
    if (!pacingConfig.enabled) {
        return {
            pacingEnabled: false,
            gaiaCallCategory: gaiaCallCategory ?? null,
            pacingActualSeconds: actualSeconds,
            salesEligible,
            eligibleProductCount,
            hasPrimaryProduct,
        };
    }

    // When pacing is enabled but Gaia hasn't classified the call (or classified as a category
    // without a specific configured target), fall back to the partner's defaultTargetSeconds
    // and flag the analytics event so consumers can distinguish default vs category-specific targets.
    const usingDefaultTarget = categoryTarget === undefined;
    const baseTarget = categoryTarget ?? pacingConfig.defaultTargetSeconds;
    const targetSeconds = baseTarget + (salesEligible ? pacingConfig.salesEligibleBufferSeconds : 0);

    const fields: Record<string, unknown> = {
        pacingEnabled: true,
        gaiaCallCategory: gaiaCallCategory ?? null,
        pacingBaseTargetSeconds: baseTarget,
        pacingTargetSeconds: targetSeconds,
        pacingUsingDefaultTarget: usingDefaultTarget,
        salesEligible,
        eligibleProductCount,
        hasPrimaryProduct,
    };

    if (actualSeconds !== undefined) {
        fields.pacingActualSeconds = actualSeconds;
        fields.pacingResult = actualSeconds <= targetSeconds ? "on_target" : "exceeded";
        if (actualSeconds > targetSeconds) {
            fields.pacingOverageSeconds = actualSeconds - targetSeconds;
        }
    }

    return fields;
}
